<template>
  <DDV
    v-slot="{ enableMouseTracking, onUpdateNearestPoint, mouseState, margins }"
    v-bind="$attrs"
    class="pump-performance"
    :resize-throttle-ms="100"
    @update:dom-state="domState = $event"
  >
    <template v-if="domState">
      <rect
        v-if="domState.plotWidth > 0"
        :x="margins.left"
        :y="margins.top"
        :width="domState.plotWidth"
        :height="domState.plotHeight"
        fill="var(--plotSurface)"
      />
      <template v-if="npshrPoints.length">
        <!-- Target NPSHa OP -->
        <CartesianScaler
          v-if="domState.plotWidth > 0"
          v-slot="{ xScaler, yScaler }"
          :x-source="aggregateNPSHFlowRateDomain"
          :y-source="aggregateNPSHHeadDomain"
          :x-target="targetDomainX"
          :y-target="targetDomainY"
          reverse-y-axis
        >
          <Axis
            :x="margins.left"
            :y="margins.top"
            :domain="aggregateNPSHFlowRateDomain"
            :label="dataSetNpsha?.xAxisLabel"
            :scaler="xScaler"
            :tick-frequency="ddvTickFrequencies?.x"
            draw-grid-lines
          />
          <Axis
            :x="margins.left"
            :y="margins.top"
            :domain="aggregateNPSHHeadDomain"
            :label="dataSetNpsha?.yAxisLabel"
            :scaler="yScaler"
            :tick-frequency="ddvTickFrequencies?.y"
            is-vertical
            draw-grid-lines
          />
          <g
            v-if="activeOperatingPoints[0] && dataSetTargetNpshaOperatingPoint"
            :transform="`translate(${margins.left} ${margins.top})`"
          >
            <g
              :transform="`
            translate(
              ${xScaler.scale(dataSetTargetNpshaOperatingPoint.point.x)},
              ${yScaler.scale(dataSetTargetNpshaOperatingPoint.point.y)}
            )`"
            >
              <circle
                :cx="0"
                :cy="0"
                :r="5"
                stroke="var(--activePlotColor)"
                :stroke-width="2"
                fill="transparent"
              />
              <circle
                :cx="0"
                :cy="0"
                :r="5"
                stroke="var(--activePlotColor)"
                :stroke-opacity="0.3"
                :stroke-width="8"
                fill="transparent"
              />
              <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
            </g>
          </g>
        </CartesianScaler>

        <!-- NPSHr Curve -->
        <CartesianScaler
          v-if="domState.plotWidth > 0"
          :x-source="aggregateNPSHFlowRateDomain"
          :y-source="aggregateNPSHHeadDomain"
          :x-target="targetDomainX"
          :y-target="targetDomainY"
          reverse-y-axis
        >
          <g :transform="`translate(${margins.left} ${margins.top})`">
            <LinePlot
              :is-active="activeDataSets[0]"
              :data-series-number="1"
              :screen-points="screenPointsNPSHr"
              curve-interpolator="straight"
            />
            <BSP
              v-slot="{ nearestPoint }"
              ignore-y-values
              :points="dataSetNpshr?.points"
              :screen-points="screenPointsNPSHr"
              :track-nearest-point="enableMouseTracking && activeDataSets[0]"
              @update:nearest-point="
                onUpdateNearestPoint(
                  dataSetNpshr?.title ?? '',
                  1,
                  activeDataSets[0],
                  $event,
                  formatValue,
                  formatValue,
                  `(${targetXUnitLabel})`,
                  `(${targetYUnitLabel})`
                )
              "
            >
              <NearestPoint
                v-if="nearestPoint?.node"
                :data-series-number="1"
                :nearest-point="nearestPoint"
              />
            </BSP>
          </g>
        </CartesianScaler>
      </template>
      <template v-else>
        <text
          fill="var(--plotColor0)"
          x="50%"
          y="50%"
          dominant-baseline="middle"
          text-anchor="middle"
        >
          No Data
        </text>
      </template>

      <ChartTitle :title="title ? `${title} NPSH` : 'NPSH'" />

      <Crosshairs
        v-if="readyToDraw(enableMouseTracking, mouseState.isHovered)"
        ignore-y
        :x="margins.left"
        :y="margins.top"
      />
    </template>
  </DDV>
  <div>
    <CustomLegend
      v-if="drawLegend"
      v-model="activeOperatingPoints"
      :model="operatingPoints"
      :static="isStatic"
      :horizontal="isHorizontal"
    />
    <CustomLegend
      v-if="drawLegend"
      v-model="activeDataSets"
      :model="npshDataSets"
      :static="isStatic"
      :horizontal="isHorizontal"
    />
    <div v-if="npshrAtWellRate" class="legend-extra">
      NPSHr at {{ formatValue(npshrAtWellRate.x) }} {{ targetXUnitLabel }}:
      {{ formatValue(npshrAtWellRate.y) }}
      {{ targetYUnitLabel }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type { DOMState, NumericPoint } from "@incytestudios/ddv/types/common";
import CustomLegend from "./CustomLegend.vue";

// Eslint auto fixing is causing issues with this import
// eslint-disable-next-line
import {
  Axis,
  fromRange,
  DDV,
  BSP,
  LinePlot,
  ChartTitle,
  Crosshairs,
  NearestPoint,
  CartesianScaler,
  getDefaultValueFormatter,
  useScreenPoints,
  useLinearScaler
} from "@incytestudios/ddv";

import type { Scenario } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { NumericDomain } from "@incytestudios/ddv/types/domain";
import type { SinglePumpChartData } from "@/libs/charting/d";
import { useSinglePumpChartData } from "@/libs/charting/single/useSinglePumpChartData";
import {
  scenarioKey,
  sizingKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import type { OperatingPoint } from "@/components/types";
import type {
  DataSetWithOperatingPoint,
  DataSetWithOriginalPoints
} from "@/libs/charting";
import { HorsePower } from "@/libs/units/power";

const props = defineProps<{
  title?: string;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  chartData?: SinglePumpChartData | undefined;
  drawLegend?: boolean;
  isHorizontal?: boolean;
  isStatic?: boolean;
}>();

const _scenario = props.scenario
  ? ref<Scenario>(props.scenario)
  : injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const { getLiquidFlowUnit, getLargeLengthUnit, getPressureUnit } =
  useScenarioUnits(_scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit("Pump Intake Pressure.convert.PSIA");

const readyToDraw = (enableMouseTracking: boolean, isHovered: boolean) => {
  return (
    enableMouseTracking &&
    isHovered &&
    npshaPoints.value.length &&
    npshrPoints.value.length
  );
};

// Custom pixels to squash the chart
const domState = ref<DOMState>({
  plotWidth: 600,
  plotHeight: 400,
  elementWidth: 600,
  elementHeight: 400,
  computedHeight: 400
});

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const chartingData = computed(() => {
  return (
    props.chartData ||
    useSinglePumpChartData(
      _calcs,
      targetLiquidFlowUnit.value,
      targetLargeLengthUnit.value,
      targetPressureUnit.value,
      // For now, this is hardcoded since BHP is always in HorsePower for now
      HorsePower
    )
  );
});

const targetXUnitLabel = computed(() => {
  return chartingData.value?.targetXUnitLabel;
});
const targetYUnitLabel = computed(() => {
  return chartingData.value?.targetNpshrYUnitLabel;
});

const dataSetNpsha = computed<DataSetWithOperatingPoint | undefined>(() => {
  return chartingData.value?.dataSets.npsha;
});

const dataSetNpshr = computed<DataSetWithOriginalPoints | undefined>(() => {
  // Make copy to include color override
  if (!chartingData.value?.dataSets.npshr) {
    return undefined;
  }
  const npshr = {
    ...chartingData.value?.dataSets.npshr,
    color: "var(--plotColor1)"
  };
  return npshr;
});

const dataSetTargetNpshaOperatingPoint = computed<OperatingPoint | undefined>(
  () => {
    return dataSetNpsha.value?.operatingPoint;
  }
);

const operatingPoints = computed<OperatingPoint[]>(() => {
  const points: OperatingPoint[] = [];
  if (dataSetTargetNpshaOperatingPoint.value) {
    points.push(dataSetTargetNpshaOperatingPoint.value);
  }
  return points;
});
const npshDataSets = computed(() => {
  if (!dataSetNpshr.value) {
    return undefined;
  } else {
    return [dataSetNpshr.value];
  }
});

const npshaPoints = computed<NumericPoint[]>(() => {
  return (dataSetNpsha.value?.points || []) as NumericPoint[];
});

const npshrPoints = computed<NumericPoint[]>(() => {
  return (dataSetNpshr.value?.points || []) as NumericPoint[];
});

// Need to check from original points since target rate could be different unit
const npshrAtWellRate = computed(() => {
  if (!npshrPoints.value.length) {
    return undefined;
  }
  const npshrAtWellRatePoint = dataSetNpshr.value?.originalPoints.find(
    point =>
      point.x === _scenario.value?.parameters?.target_liquid_rate_std_barrel_day
  );

  if (npshrAtWellRatePoint) {
    return {
      x: targetLiquidFlowUnit.value.toThisUnit(npshrAtWellRatePoint.x),
      y: targetLargeLengthUnit.value.toThisUnit(npshrAtWellRatePoint.y)
    };
  }
  return npshrAtWellRatePoint;
});

const targetDomainX = computed(() => fromRange(domState.value?.plotWidth || 0));
const targetDomainY = computed(() =>
  fromRange(domState.value?.plotHeight || 0)
);

const formatValue = getDefaultValueFormatter();

const activeDataSets = ref([true]);
const activeOperatingPoints = ref([true]);
const aggregateNPSHFlowRateDomain = computed<NumericDomain>(() => ({
  min: 0,
  max:
    Math.max(
      ...(dataSetNpsha.value?.points || []).map(({ x }) =>
        typeof x === "number" ? x : 0
      ),
      ...(dataSetNpshr.value?.points || []).map(({ x }) =>
        typeof x === "number" ? x : 0
      )
    ) * 1.1
}));

const aggregateNPSHHeadDomain = computed<NumericDomain>(() => ({
  min: 0,
  max:
    Math.max(
      ...(dataSetNpsha.value?.points || []).map(({ y }) =>
        typeof y === "number" ? y : 0
      ),
      ...(dataSetNpshr.value?.points || []).map(({ y }) =>
        typeof y === "number" ? y : 0
      )
    ) * 1.1
}));

const npshFlowRateScaler = useLinearScaler(
  aggregateNPSHFlowRateDomain,
  targetDomainX,
  false
);
const npshHeadScaler = useLinearScaler(
  aggregateNPSHHeadDomain,
  targetDomainY,
  true
);

const { screenPoints: screenPointsNPSHr } = useScreenPoints(
  npshrPoints,
  npshFlowRateScaler,
  npshHeadScaler
);
</script>

<style lang="scss" scoped>
.pump-performance {
  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}

.legend-extra {
  padding-left: 0.5rem;
}
</style>
